import {
  Search as SearchIcon,
  Badge as BadgeIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  InputAdornment,
  TextField,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { roleManagementApi } from "apis";
import ConfirmationPopup from "components/common/ConfirmationPopup";
import CreateRolePopup from "components/common/CreateRolePopup";
import CustomTable, { CustomTableColumn } from "components/common/CustomTable";
import FilterDropdown, { FilterOption } from "components/common/FilterDropdown";
import DashboardLayout from "components/layouts/DashboardLayout";
import { useToast } from "contexts/ToastContext";
import { formatDate } from "helpers/dateTimeHelper";
import {
  CreateRoleRequestDto,
  GetRolesOrderByDirectionEnum,
  GetRolesOrderByEnum,
  GetRolesRequest,
  RoleLightDto,
  UpdateRoleRequest,
} from "libs/cellera-sdk";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";

import { AssignToModal } from "./components/AssignToModal";

const filterConstants = {
  noUsers: { value: "noUsers", label: "No Users" },
  hasUsers: { value: "hasUsers", label: "Has Users" },
};

export const RoleManagementPage: React.FC = () => {
  const { showError, showSuccess } = useToast();
  const { colors } = useCelleraTheme();
  const [isCreateLoading, setIsCreateLoading] = useState(false);
  const [isCreatePopupOpen, setIsCreatePopupOpen] = useState(false);
  const [request, setRequest] = useState<GetRolesRequest>({
    page: 1,
    pageSize: 10,
    search: "",
    hasUsers: undefined,
    orderBy: GetRolesOrderByEnum.CreatedAt,
    orderByDirection: GetRolesOrderByDirectionEnum.Desc,
  });

  const {
    data: rolesData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["roles", request],
    queryFn: async () => {
      const response = await roleManagementApi.getRoles(request);
      return {
        items: response.items,
        totalCount: response.pagination.total,
      };
    },
    retry: 2,
  });

  const data = rolesData?.items || [];
  const totalCount = rolesData?.totalCount || 0;
  const [selectedRoleId, setSelectedRoleId] = useState<string | undefined>(
    undefined
  );
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<RoleLightDto | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRole, setSelectedRole] = useState<RoleLightDto | null>(null);
  const [isAssignToModalOpen, setIsAssignToModalOpen] = useState(false);
  const [selectedRoleForAssign, setSelectedRoleForAssign] = useState<
    string | null
  >(null);

  const columns: CustomTableColumn<RoleLightDto>[] = useMemo(
    () => [
      {
        id: "name",
        label: "Name",
        getValue: (row) => row.name,
      },
      {
        id: "description",
        label: "Description",
        getValue: (row) => row.description,
      },
      {
        id: "assign_to",
        label: "Assign To",
        getValue: (row) => (
          <Box onClick={() => handleAssignToClick(row)}>
            {row.providerIds.length}
          </Box>
        ),
      },
      {
        id: "added_date",
        label: "Added Date",
        getValue: (row) => formatDate(row.createdOn.toISOString()),
      },
      {
        id: "updated_date",
        label: "Updated Date",
        getValue: (row) => formatDate(row.updatedOn.toISOString()),
      },
      {
        id: "actions",
        label: "",
        getValue: (row) => (
          <IconButton
            size="small"
            onClick={(event) => handleMenuClick(event, row)}
            sx={{
              color: colors.text.secondary,
              "&:hover": {
                backgroundColor: colors.background.secondary,
                color: colors.text.primary,
              },
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRequest((prev) => ({ ...prev, search: event.target.value }));
    },
    []
  );

  // Handle query errors
  useEffect(() => {
    if (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to fetch data";
      showError(errorMessage);
    }
  }, [error, showError]);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRequest((prev) => ({
      ...prev,
      pageSize: parseInt(event.target.value, 10),
    }));
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setRequest((prev) => ({ ...prev, page: newPage + 1 }));
  };

  const handleCreateRole = useCallback(
    async (roleData: {
      name: string;
      description: string;
      rightsIds: string[];
    }) => {
      try {
        setIsCreateLoading(true);
        if (selectedRoleId) {
          const updateRoleRequest: UpdateRoleRequest = {
            id: selectedRoleId,
            updateRoleRequestDto: {
              id: selectedRoleId,
              name: roleData.name,
              description: roleData.description || undefined,
              rightsIds: roleData.rightsIds,
            },
          };
          await roleManagementApi.updateRole(updateRoleRequest);
        } else {
          const createRoleRequest: CreateRoleRequestDto = {
            name: roleData.name,
            description: roleData.description || undefined,
            rightsIds: roleData.rightsIds,
          };
          await roleManagementApi.createRole({
            createRoleRequestDto: createRoleRequest,
          });
        }
        showSuccess("Role created successfully!");
        setIsCreatePopupOpen(false);
        refetch();
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to create role";
        showError(errorMessage);
      } finally {
        setIsCreateLoading(false);
        setSelectedRoleId(undefined);
      }
    },
    [selectedRoleId, refetch, showError, showSuccess]
  );

  const handleOpenCreatePopup = () => {
    setSelectedRoleId(undefined);
    setIsCreatePopupOpen(true);
  };

  const handleCloseCreatePopup = () => {
    setSelectedRoleId(undefined);
    setIsCreatePopupOpen(false);
  };

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    role: RoleLightDto
  ) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedRole(role);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedRole(null);
  };

  const handleEditRole = (roleId: string) => {
    setSelectedRoleId(roleId);
    setIsCreatePopupOpen(true);
    handleMenuClose();
  };

  const handleDeleteClick = (role: RoleLightDto) => {
    setRoleToDelete(role);
    setIsDeleteConfirmOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = useCallback(async () => {
    if (!roleToDelete) return;

    try {
      setIsDeleting(true);
      await roleManagementApi.deleteRole({ id: roleToDelete.id });
      showSuccess("Role deleted successfully!");
      setIsDeleteConfirmOpen(false);
      setRoleToDelete(null);

      refetch();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to delete role";
      showError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  }, [roleToDelete, refetch, showError, showSuccess]);

  const handleDeleteCancel = useCallback(() => {
    setIsDeleteConfirmOpen(false);
    setRoleToDelete(null);
  }, []);

  const handleAssignToClick = useCallback((role: RoleLightDto) => {
    setSelectedRoleForAssign(role.id);
    setIsAssignToModalOpen(true);
  }, []);

  const handleCloseAssignToModal = useCallback(() => {
    setIsAssignToModalOpen(false);
    setSelectedRoleForAssign(null);
  }, []);

  const filterOptions: FilterOption[] = useMemo(
    () => [filterConstants.noUsers, filterConstants.hasUsers],
    []
  );

  const handleFilterApply = (filters: string[]) => {
    let hasUsers: boolean | undefined = undefined;
    if (filters.includes(filterConstants.noUsers.value)) {
      hasUsers = false;
    }
    if (filters.includes(filterConstants.hasUsers.value)) {
      hasUsers = true;
    }
    if (filters.includes("noUsers") && filters.includes("hasUsers")) {
      hasUsers = undefined;
    }
    setRequest((prev) => ({ ...prev, hasUsers }));
  };

  return (
    <DashboardLayout
      title="Role & Permission"
      sideButton={
        <Button
          variant="contained"
          onClick={handleOpenCreatePopup}
          className="rounded-full px-6 py-2 font-inter text-white transition-all duration-200 hover:shadow-lg"
        >
          Add
        </Button>
      }
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          backgroundColor: colors.background.paper,
          border: `1px solid ${colors.border.light}`,
          borderRadius: "8px",
          p: 0,
          height: "calc(100vh - 100px)",
        }}
      >
        <Box sx={{ width: "100%" }}>
          <Box
            component="div"
            className="flex !w-full !py-3 !px-4"
            justifyContent="space-between"
          >
            {/* Search Input */}
            <TextField
              fullWidth
              placeholder="Search"
              value={request.search}
              onChange={handleSearchChange}
              size="small"
              sx={{ width: "400px" }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: colors.text.secondary }} />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <FilterDropdown
              filterOptions={filterOptions}
              onApply={handleFilterApply}
            />
          </Box>
          <CustomTable
            columns={columns}
            data={data}
            totalCount={totalCount}
            page={(request.page ?? 1) - 1}
            isLoading={isLoading}
            rowsPerPage={request.pageSize ?? 10}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            loadingMessage="Loading role data..."
            emptyStateContent={
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <BadgeIcon
                  sx={{
                    color: "text.disabled",
                    fontSize: 48,
                    opacity: 0.5,
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 500,
                  }}
                >
                  No role available
                </Typography>
              </Box>
            }
            paperSx={{
              height: "calc(100% - 70px)",
              borderRadius: 0,
              border: "none",
              justifyContent: "space-between",
            }}
          />
        </Box>
      </Box>
      {/* Create Role Popup */}
      <CreateRolePopup
        open={isCreatePopupOpen}
        onClose={handleCloseCreatePopup}
        onSubmit={handleCreateRole}
        isLoading={isCreateLoading}
        editingRoleId={selectedRoleId}
      />
      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "8px",
              border: `1px solid ${colors.border.light}`,
              minWidth: 160,
            },
          },
        }}
      >
        <MenuItem
          onClick={() => selectedRole && handleEditRole(selectedRole.id)}
          sx={{
            color: colors.text.primary,
            "&:hover": {
              backgroundColor: colors.background.secondary,
            },
          }}
        >
          <ListItemText primary="View details" />
        </MenuItem>
        <MenuItem
          onClick={() => selectedRole && handleDeleteClick(selectedRole)}
          sx={{
            color: colors.text.primary,
            "&:hover": {
              backgroundColor: colors.error[50],
            },
          }}
        >
          <ListItemText primary="Delete" />
        </MenuItem>
      </Menu>
      {/* Delete Confirmation Popup */}
      <ConfirmationPopup
        open={isDeleteConfirmOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Role & Permissions"
        message="Are you sure you want to delete this role? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor={colors.error[700]}
        loading={isDeleting}
        iconColor={colors.text.inverse}
        iconBackgroundColor={colors.error[500]}
      />
      {/* Assign To Modal */}
      {selectedRoleForAssign && (
        <AssignToModal
          open={isAssignToModalOpen}
          onClose={handleCloseAssignToModal}
          roleId={selectedRoleForAssign}
        />
      )}
    </DashboardLayout>
  );
};
